import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import type { SetupChecklist } from '@/features/platform/types'
import { cn } from '@/utils/cn'
import { SETUP_STEPS } from './steps'

/** The steps of the setup guide, each with where to do it; the next step to do is highlighted. */
export function SetupChecklistView({
  checklist,
  compact = false,
}: {
  checklist: SetupChecklist
  compact?: boolean
}) {
  const { t } = useTranslation('setup')
  const next = checklist.steps.find((s) => !s.done && !s.optional) ?? checklist.steps.find((s) => !s.done)
  const steps = compact ? checklist.steps.filter((s) => !s.optional) : checklist.steps
  return (
    <ol className="divide-y divide-line">
      {steps.map((step, i) => {
        const meta = SETUP_STEPS[step.key]
        const Icon = meta.icon
        const isNext = next?.key === step.key
        return (
          <li
            key={step.key}
            className={cn(
              'flex flex-wrap items-start gap-3 px-5 py-3.5 sm:flex-nowrap',
              isNext && 'bg-brand-50/50',
            )}
          >
            <span
              className={cn(
                'grid size-9 shrink-0 place-items-center rounded-full ring-1',
                step.done
                  ? 'bg-success-50 text-success-600 ring-success-500/20'
                  : 'bg-surface text-muted ring-line',
              )}
              aria-hidden="true"
            >
              {step.done ? <CheckCircle2 className="size-5" /> : <Icon className="size-4" />}
            </span>
            <div className="min-w-0 flex-1 max-sm:basis-4/5">
              <p className={cn('text-sm font-medium', step.done ? 'text-ink-soft' : 'text-ink')}>
                <span className="sr-only">{t('Step {{n}}', { n: i + 1 })} · </span>
                {t(meta.title)}
                {step.optional ? <span className="font-normal text-muted"> · {t('optional')}</span> : null}
              </p>
              {!compact || isNext ? <p className="mt-0.5 text-xs text-muted">{t(meta.description)}</p> : null}
            </div>
            {step.done ? (
              <Badge tone="success" size="sm" className="max-sm:ml-12">
                {t('Done')}
              </Badge>
            ) : (
              <Button
                asChild
                className="max-sm:ml-12"
                size="sm"
                variant={isNext ? 'primary' : 'ghost'}
                rightIcon={isNext ? <ArrowRight /> : undefined}
              >
                <Link to={meta.href}>{t(meta.action)}</Link>
              </Button>
            )}
          </li>
        )
      })}
    </ol>
  )
}
